import { useState, useEffect, useContext, useRef, useCallback } from "react";
import { Button, Tooltip } from "@fluentui/react-components";
import { useTranslation } from "react-i18next";

import styles from "./QuestionInput.module.css";
import vectorIcon from "../../assets/Vector.png";
import { SpeechInput } from "./SpeechInput";
import { LoginContext } from "../../loginContext";
import { requireLogin } from "../../authConfig";

interface Props {
    onSend: (question: string) => void;
    disabled: boolean;
    initQuestion?: string;
    placeholder?: string;
    clearOnSend?: boolean;
    showSpeechInput?: boolean;
    multiline?: boolean;
    expandUp?: boolean;
}

export const QuestionInput = ({ onSend, disabled, placeholder, clearOnSend, initQuestion, showSpeechInput, expandUp }: Props) => {
    const [question, setQuestion] = useState<string>("");
    const { loggedIn } = useContext(LoginContext);
    const { t } = useTranslation();
    const [isComposing, setIsComposing] = useState(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const autoResize = useCallback(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.setProperty("height", "auto", "important");

        let maxH = 200;
        if (expandUp) {
            // Cap so the sticky input section + filters never overflow the viewport
            const stickySection = el.closest("[data-sticky-input]");
            if (stickySection) {
                const scrollParent = el.closest("[data-chat-scroll]");
                const viewportH = scrollParent ? scrollParent.clientHeight : window.innerHeight;
                const currentTextareaH = el.offsetHeight;
                const stickyH = stickySection.scrollHeight - currentTextareaH;
                // Leave at least 100px for messages above
                maxH = Math.max(60, viewportH - stickyH - 100);
                maxH = Math.min(maxH, 200);
            }
        }

        const h = Math.min(el.scrollHeight, maxH);
        el.style.setProperty("height", h + "px", "important");
        el.style.setProperty("max-height", maxH + "px", "important");
        el.style.setProperty("overflow-y", el.scrollHeight > maxH ? "auto" : "hidden", "important");

        if (expandUp) {
            const scrollContainer = el.closest("[data-chat-scroll]");
            if (scrollContainer) {
                scrollContainer.scrollTop = scrollContainer.scrollHeight;
            }
        }
    }, [expandUp]);

    useEffect(() => {
        initQuestion && setQuestion(initQuestion);
    }, [initQuestion]);

    useEffect(() => {
        autoResize();
    }, [question, autoResize]);

    const sendQuestion = () => {
        if (disabled || !question.trim()) {
            return;
        }

        onSend(question);

        if (clearOnSend) {
            setQuestion("");
        }
    };

    const onEnterPress = (ev: React.KeyboardEvent<Element>) => {
        if (isComposing) return;

        if (ev.key === "Enter" && !ev.shiftKey) {
            ev.preventDefault();
            sendQuestion();
        }
    };

    const handleCompositionStart = () => {
        setIsComposing(true);
    };
    const handleCompositionEnd = () => {
        setIsComposing(false);
    };

    const onQuestionChange = (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const newValue = ev.target.value;
        if (newValue.length <= 20000) {
            setQuestion(newValue);
        }
    };

    const disableRequiredAccessControl = requireLogin && !loggedIn;
    const sendQuestionDisabled = disabled || !question.trim() || disableRequiredAccessControl;

    if (disableRequiredAccessControl) {
        placeholder = "Please login to continue...";
    }

    return (
        <div className={styles.questionInputContainer}>
            <div className={styles.questionInputTextArea}>
                <textarea
                    ref={textareaRef}
                    className={styles.questionTextarea}
                    disabled={disableRequiredAccessControl}
                    placeholder={placeholder}
                    value={question}
                    onChange={onQuestionChange}
                    onKeyDown={onEnterPress}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    rows={1}
                />
            </div>
            <div className={styles.questionInputButtonsContainer}>
                <Tooltip content="Submit question" relationship="label">
                    <Button
                        size="large"
                        className={styles.submitButton}
                        icon={<img src={vectorIcon} alt="Submit" className={styles.submitIcon} />}
                        disabled={sendQuestionDisabled}
                        onClick={sendQuestion}
                    />
                </Tooltip>
            </div>
            {showSpeechInput && <SpeechInput updateQuestion={setQuestion} />}
        </div>
    );
};
